import { shopShelf, type GameSnapshot } from '../engine/casino';
import { floorDef } from '../engine/floors';
import { upgradeDef, UPGRADES } from '../engine/shop';
import { money } from '../format';
import type { GameActions } from '../useGame';
import { MenuFrame } from './MenuFrame';

export function Shop({ snapshot, actions }: { snapshot: GameSnapshot; actions: GameActions }) {
  const run = snapshot.run!;
  const shelf = shopShelf(snapshot);
  const owned = snapshot.meta.upgrades.length;
  // Everything not on the shelf yet, and why (user, 2026-09-27: the shop
  // looked like it was missing items; the next tier of an upgrade and the
  // higher floors' items were simply hidden).
  const mine = snapshot.meta.upgrades;
  const comingUp = UPGRADES.filter((u) => !mine.includes(u.id) && (u.unlockFloor > snapshot.meta.highestFloor || (u.requires && !mine.includes(u.requires)))).map((u) => ({
    u,
    why: u.unlockFloor > snapshot.meta.highestFloor ? `Reach the ${floorDef(u.unlockFloor).name}` : `After ${upgradeDef(u.requires!)?.name ?? 'the tier before'}`,
  }));
  const lap = run.ownsCasino;

  return (
    <MenuFrame snapshot={snapshot} actions={actions} title="Gift Shop" backLabel={lap ? 'Finish the run' : 'Back to the floor'}>
      <p className="menu-intro">
        {lap
          ? 'One last trip: everything here is for your next run. Leave when you’re done.'
          : 'Upgrades for every run after this one, paid with this run’s cash.'}
        {owned > 0 ? ` You own ${owned}.` : ''}
      </p>
      <ul className="menu-list" aria-label="Gift Shop">
        {shelf.map((item, i) => {
          const casino = item.id === 'casino';
          const confirming = snapshot.shopConfirm === item.id;
          const afford = item.price <= run.bankroll;
          const needs = item.requires ? upgradeDef(item.requires)?.name : null;
          return (
            <li key={item.id}>
              <button className={`menu-item${casino ? ' casino' : ''}${afford ? '' : ' off'}${confirming ? ' confirming' : ''}`} onClick={() => actions.buyItem(i)}>
                <span className="item-top">
                  <span className="item-name">{item.name}</span>
                  <span className="item-price">{money(item.price)}</span>
                </span>
                <span className="item-effect">{item.effect}</span>
                <span className="item-meta">
                  <span>{needs ? `Upgrades ${needs}` : casino ? 'Wins the run' : 'Starts next run'}</span>
                  <span className={`badge${afford ? ' ready' : ''}`}>{confirming ? 'Tap again to buy' : afford ? 'Buy' : `Need ${money(item.price - run.bankroll)} more`}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {shelf.length === 0 && <p className="menu-intro">You own everything on these shelves.</p>}
      {comingUp.length > 0 && (
        <details className="coming-up">
          <summary>Coming up · {comingUp.length} more {comingUp.length === 1 ? 'item' : 'items'}</summary>
          <ul aria-label="Not on the shelves yet">
            {comingUp.map(({ u, why }) => (
              <li key={u.id}>
                <span className="cu-name">{u.name}</span>
                <span className="cu-price">{money(u.price)}</span>
                <span className="cu-why">{why}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </MenuFrame>
  );
}

export function Owner({ snapshot, actions }: { snapshot: GameSnapshot; actions: GameActions }) {
  return (
    <div className="center-screen">
      <div className="block paper dialog">
        <h2>The casino is yours.</h2>
        <p>You have {money(snapshot.run?.bankroll ?? 0)} left. Spend it in the Gift Shop on upgrades for your next run, or call it a night.</p>
        <div className="dialog-buttons">
          <button className="btn btn-secondary" onClick={() => actions.ownerChoice(true)}>Gift Shop</button>
          <button className="btn btn-primary" onClick={() => actions.ownerChoice(false)}>I’m done</button>
        </div>
      </div>
    </div>
  );
}
